import React, { useState } from "react";
import { createPortal } from "react-dom";
import { doc, updateDoc } from "firebase/firestore";
import { db, auth } from "../firebase";

const STATUS_OPTIONS = [
    { type: "online", label: "Available", icon: "🟢" },
    { type: "busy", label: "Busy", icon: "⛔" },
    { type: "study", label: "Study Mode", icon: "📚" },
    { type: "driving", label: "Driving", icon: "🚗" },
    { type: "meeting", label: "In a Meeting", icon: "📅" },
    { type: "gym", label: "At the Gym", icon: "🏋️" },
    { type: "sleeping", label: "Sleeping", icon: "😴" }
];

export default function StatusSelector({ onClose, currentStatus }) {
    const [customText, setCustomText] = useState(currentStatus?.text || "");
    const [selectedType, setSelectedType] = useState(currentStatus?.type || "online");

    const handleSave = async () => {
        if (!auth.currentUser) return;

        const statusData = {
            type: selectedType,
            text: selectedType === "custom" ? customText : STATUS_OPTIONS.find(o => o.type === selectedType)?.label || "",
            icon: STATUS_OPTIONS.find(o => o.type === selectedType)?.icon || "✏️",
            updatedAt: Date.now()
        };

        try {
            await updateDoc(doc(db, "users", auth.currentUser.uid), {
                status: statusData
            });
            onClose();
        } catch (err) {
            console.error("Failed to update status:", err);
            alert("Failed to update status. Please try again.");
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bg-[#00a884] p-4 text-white flex justify-between items-center">
                    <h3 className="font-bold text-lg">Set Status</h3>
                    <button onClick={onClose} className="hover:bg-white/20 rounded-full p-1 transition">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        {STATUS_OPTIONS.map((option) => (
                            <button
                                key={option.type}
                                onClick={() => setSelectedType(option.type)}
                                className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-2 transition-all ${selectedType === option.type
                                        ? "bg-green-50 border-[#00a884] shadow-md transform scale-105"
                                        : "border-gray-100 hover:bg-gray-50 hover:border-gray-200"
                                    }`}
                            >
                                <span className="text-2xl">{option.icon}</span>
                                <span className={`text-sm font-medium ${selectedType === option.type ? "text-[#00a884]" : "text-gray-600"}`}>
                                    {option.label}
                                </span>
                            </button>
                        ))}
                        {/* Custom Option */}
                        <button
                            onClick={() => setSelectedType("custom")}
                            className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-2 transition-all ${selectedType === "custom"
                                    ? "bg-green-50 border-[#00a884] shadow-md transform scale-105"
                                    : "border-gray-100 hover:bg-gray-50 hover:border-gray-200"
                                }`}
                        >
                            <span className="text-2xl">✏️</span>
                            <span className={`text-sm font-medium ${selectedType === "custom" ? "text-[#00a884]" : "text-gray-600"}`}>
                                Custom
                            </span>
                        </button>
                    </div>

                    {selectedType === "custom" && (
                        <div className="mt-2">
                            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Custom Status</label>
                            <input
                                type="text"
                                placeholder="What's happening?"
                                value={customText}
                                onChange={(e) => setCustomText(e.target.value)}
                                maxLength={30}
                                className="w-full mt-1 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00a884]/50 focus:border-[#00a884] transition"
                                autoFocus
                            />
                        </div>
                    )}

                    <button
                        onClick={handleSave}
                        className="w-full bg-[#00a884] hover:bg-[#008f6f] text-white font-bold py-3 rounded-xl shadow-lg transition-all active:scale-95 mt-2"
                    >
                        Update Status
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
